<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>自動応答チャットメッセージの複製</template>
    </SettingsPagePanelHeader>

    <section :class="$style.wrapper">
      <p :class="$style.warning">
        日程調整に設定されている日程調整カレンダーは複製先に引き継がれません。<br />日程調整カレンダーをご利用になる場合は複製後の再設定が必要です。
      </p>
      <p :class="$style.sectionTitle">複製元のアドバイザーを選択</p>
      <DolphinSelector
        :class="$style.selector"
        :options="sources"
        v-model="selectedSourcePlannerId"
        placeholder="アドバイザーを選択してください"
      />
      <p :class="$style.sectionTitle">複製先のアドバイザーを選択（複数可）</p>
      <p :class="$style.note">
        ※自動応答チャットメッセージの設定がないアドバイザーのみ表示されます
      </p>
      <ul :class="$style.destinations">
        <li v-for="(item, index) in destinations" :key="index">
          <label :class="$style.listItem">
            <DolphinCheckbox
              :value="item.value"
              v-model="selectedDestinationPlannerIds"
              :isDisabled="item.isDisabled"
            />
            <span>{{ item.text }}</span>
          </label>
        </li>
      </ul>
    </section>

    <footer :class="$style.footer">
      <FlatButton @click="handleCopy">複製</FlatButton>
    </footer>

    <ConfirmationModal>
      <AnswerMessageAutoChatReplyCopyConfirmationModal
        v-if="selectedSourcePlanner && selectedDestinations"
        :sourcePlanner="selectedSourcePlanner"
        :destinations="selectedDestinations"
        @cancel="handleCancel"
        @confirm="handleConfirm"
      />
    </ConfirmationModal>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'

import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import { useModal } from '@/hooks/useModal'
import AnswerMessageAutoChatReplyCopyConfirmationModal from './AnswerMessageAutoChatReplyCopyConfirmationModal.vue'
import {
  useFetchPlannersInSameCorporationQuery,
  useFetchConflictsOfAutoChatReplySettingsBetweenSrcAndDstLazyQuery,
  useCopyAutoChatReplySettingsMutation,
} from './SettingsPageAutoChatReplyCopyView.generated'
import { AutoChatReplySettingCopyStrategy } from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'

const selectedSourcePlannerId = ref<Maybe<string>>(undefined)

const selectedDestinationPlannerIds = ref<string[]>([])

const { result, refetch } = useFetchPlannersInSameCorporationQuery()

const planners = computed(() => result.value?.plannersInSameCorporation)

const sources = computed(() =>
  planners.value
    ?.filter(value => value.autoChatReplySettings.length > 0)
    .map(value => ({
      value: value.id,
      text: value.name,
    }))
)

const destinations = computed(() =>
  planners.value
    ?.filter(value => value.autoChatReplySettings.length === 0)
    .map(value => ({
      value: value.id,
      text: value.name,
      isDisabled: selectedSourcePlannerId.value === value.id,
    }))
)

const selectedDestinations = computed(() =>
  destinations.value?.filter(
    destination =>
      selectedDestinationPlannerIds.value.includes(destination.value) &&
      !destination.isDisabled
  )
)

const selectedSourcePlanner = computed(() => {
  if (!selectedSourcePlannerId.value) return
  const selected = sources.value?.find(
    source => source.value === selectedSourcePlannerId.value
  )
  if (!selected) return
  return {
    id: selected.value,
    name: selected.text,
  }
})

const { openConfirmationModal, closeConfirmationModal, ConfirmationModal } =
  useModal('confirmation')

const handleCopy = () => {
  if (!selectedSourcePlanner.value) {
    window.alert('複製元のアドバイザーを選択してください')
    return
  }

  if (selectedDestinationPlannerIds.value.length === 0) {
    window.alert('複製先のアドバイザーを選択してください')
    return
  }

  openConfirmationModal()
}

const handleCancel = () => {
  closeConfirmationModal()
}

const alert = useAlert()

const {
  load: loadConflictsQuery,
  result: fetchConflictsQueryResult,
  refetch: refetchConflictsQuery,
} = useFetchConflictsOfAutoChatReplySettingsBetweenSrcAndDstLazyQuery({
  srcPlannerId: selectedSourcePlanner.value?.id ?? '',
  dstPlannerIds: selectedDestinationPlannerIds.value,
  strategy: AutoChatReplySettingCopyStrategy.Sync,
})

const loadOrRefetchConflictsQuery = async (variables: {
  srcPlannerId: string
  dstPlannerIds: string[]
  strategy: AutoChatReplySettingCopyStrategy
}): Promise<void> => {
  const loadResult = await loadConflictsQuery(null, variables)
  if (!loadResult) {
    await refetchConflictsQuery(variables)
  }
}

const handleConfirm = async (): Promise<void> => {
  if (!selectedSourcePlanner.value?.id) return
  await loadOrRefetchConflictsQuery({
    srcPlannerId: selectedSourcePlanner.value?.id ?? '',
    dstPlannerIds: selectedDestinationPlannerIds.value,
    strategy: AutoChatReplySettingCopyStrategy.Sync,
  })

  if (
    fetchConflictsQueryResult.value
      ?.conflictsOfAutoChatReplySettingsBetweenSrcAndDst?.length
  ) {
    alert.error('複製先のアドバイザーにすでに設定があります。')
  } else {
    execCopy()
  }

  selectedDestinationPlannerIds.value = []
  closeConfirmationModal()
}

const { mutate } = useCopyAutoChatReplySettingsMutation()

const execCopy = async () => {
  if (!selectedSourcePlanner.value?.id) return

  try {
    await mutate({
      srcPlannerId: selectedSourcePlanner.value.id,
      dstPlannerIds: selectedDestinationPlannerIds.value,
      strategy: AutoChatReplySettingCopyStrategy.Sync,
      settingIdsAllowedToOverwrite: selectedDestinationPlannerIds.value,
    })
  } catch {
    alert.error('複製に失敗しました')
    return
  }

  await refetch()
  alert.success('複製しました')
}
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sectionTitle {
  font-size: 16px;
  font-weight: 600;
}

.selector {
  max-width: 320px;
}

.listItem {
  display: flex;
  align-items: center;
  gap: 4px;
}

.destinations {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.note {
  font-size: 12px;
  font-weight: 400;
}

.warning {
  color: $color-caution-accent;
  font-size: 12px;
  font-weight: bold;
}

.footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid $color-border-low-emphasis;
  padding-top: 16px;
  margin-top: 16px;
}
</style>
